import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Trophy, Plus, Edit, Trash2, Star, Calendar as CalendarIcon } from 'lucide-react';
import TournamentCalendar from '../components/TournamentCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert "YYYY-MM-DD" from <input type="date"> to an ISO string the backend can parse
const dateInputToIso = (s) => {
  if (!s) return null;
  // Treat as date-only at midnight UTC
  return new Date(`${s}T00:00:00Z`).toISOString();
};

// Convert ISO/Date back to "YYYY-MM-DD" for the input
const isoToDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const TournamentManagement = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  
  const [createData, setCreateData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    is_default: false
  });

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`, {
        withCredentials: true,
      });
      setTournaments(response.data.tournaments);
    } catch (error) {
      toast.error('Failed to fetch tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (createData.start_date && createData.end_date && createData.start_date > createData.end_date) {
      toast.error('End date must be after start date');
      return;
    }
    try {
      await axios.post(
        `${API}/tournaments`,
        {
          name: createData.name,
          description: createData.description,
          start_date: dateInputToIso(createData.start_date),
          end_date: dateInputToIso(createData.end_date),
          is_default: createData.is_default,
        },
        { withCredentials: true }
      );

      toast.success('Tournament created successfully');
      setCreateOpen(false);
      setCreateData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        is_default: false
      });
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tournament');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (
      selectedTournament._start_date_input &&
      selectedTournament._end_date_input &&
      selectedTournament._start_date_input > selectedTournament._end_date_input
    ) {
      toast.error('End date must be after start date');
      return;
    }
    try {
      await axios.put(
        `${API}/tournaments/${selectedTournament.tournament_id}`,
        {
          name: selectedTournament.name,
          description: selectedTournament.description,
          start_date: dateInputToIso(selectedTournament._start_date_input),
          end_date: dateInputToIso(selectedTournament._end_date_input),
          is_active: selectedTournament.is_active,
          is_default: selectedTournament.is_default,
        },
        { withCredentials: true }
      );

      toast.success('Tournament updated successfully');
      setEditOpen(false);
      setSelectedTournament(null);
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tournament');
    }
  };

  const handleDelete = async (tournamentId) => {
    try {
      await axios.delete(`${API}/tournaments/${tournamentId}`, {
        withCredentials: true,
      });
      toast.success('Tournament deleted successfully');
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tournament');
    }
  };

  const openEditDialog = (tournament) => {
    setSelectedTournament({
      ...tournament,
      _start_date_input: isoToDateInput(tournament.start_date),
      _end_date_input: isoToDateInput(tournament.end_date),
    });
    setEditOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">
            Tournament Management
          </h1>
          <p className="text-muted-foreground">
            Create and manage tournaments for budget tracking
          </p>
        </div>
        
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-tournament-button" data-write-only>
              <Plus className="h-4 w-4 mr-2" />
              Create Tournament
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Tournament</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Tournament Name *</Label>
                <Input
                  id="create-name"
                  placeholder="e.g., IPL 2024, 2023-24, Champions League"
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-description">Description</Label>
                <Textarea
                  id="create-description"
                  placeholder="Optional description"
                  value={createData.description}
                  onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="create-start-date" className="flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Start Date
                  </Label>
                  <Input
                    id="create-start-date"
                    type="date"
                    value={createData.start_date}
                    onChange={(e) => setCreateData({ ...createData, start_date: e.target.value })}
                    data-testid="tournament-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-end-date" className="flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    End Date
                  </Label>
                  <Input
                    id="create-end-date"
                    type="date"
                    value={createData.end_date}
                    onChange={(e) => setCreateData({ ...createData, end_date: e.target.value })}
                    data-testid="tournament-end-date-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="create-default" className="cursor-pointer">
                  Set as Default Tournament
                </Label>
                <Switch
                  id="create-default"
                  checked={createData.is_default}
                  onCheckedChange={(checked) => 
                    setCreateData({ ...createData, is_default: checked })
                  }
                />
              </div>
              
              <Button type="submit" className="w-full">
                Create Tournament
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <TournamentCalendar tournaments={tournaments} />

      <Card>
        <CardHeader>
          <CardTitle>All Tournaments</CardTitle>
          <CardDescription>
            Tournaments replace fiscal years and can be used across all modules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tournaments.map((tournament) => (
                <TableRow key={tournament.tournament_id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {tournament.logo_url ? (
                        <img
                          src={tournament.logo_url}
                          alt={`${tournament.name} logo`}
                          className="h-7 w-7 object-contain rounded-sm border border-border bg-white"
                          data-testid={`tournament-logo-${tournament.tournament_id}`}
                        />
                      ) : (
                        <Trophy className="h-4 w-4 text-primary" />
                      )}
                      {tournament.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {tournament.start_date || tournament.end_date ? (
                      <span className="font-mono text-xs">
                        {fmtDate(tournament.start_date)}
                        <span className="text-muted-foreground mx-1.5">→</span>
                        {fmtDate(tournament.end_date)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs italic">Not scheduled</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {tournament.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={tournament.is_active ? 'success' : 'secondary'}>
                      {tournament.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {tournament.is_default && (
                      <Badge variant="default" className="flex items-center gap-1 w-fit">
                        <Star className="h-3 w-3" />
                        Default
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(tournament)}
                        data-testid={`tournament-edit-${tournament.tournament_id}`}
                        aria-label={`Edit ${tournament.name}`}
                        title="Edit tournament"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Tournament</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{tournament.name}"? 
                              This cannot be undone if there's associated data.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(tournament.tournament_id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {tournaments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No tournaments created yet. Click "Create Tournament" to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Tournament Dialog */}
      {selectedTournament && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Tournament</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Tournament Name *</Label>
                <Input
                  id="edit-name"
                  value={selectedTournament.name}
                  onChange={(e) => setSelectedTournament({ ...selectedTournament, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={selectedTournament.description || ''}
                  onChange={(e) => setSelectedTournament({ ...selectedTournament, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-start-date" className="flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Start Date
                  </Label>
                  <Input
                    id="edit-start-date"
                    type="date"
                    value={selectedTournament._start_date_input || ''}
                    onChange={(e) =>
                      setSelectedTournament({ ...selectedTournament, _start_date_input: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-end-date" className="flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    End Date
                  </Label>
                  <Input
                    id="edit-end-date"
                    type="date"
                    value={selectedTournament._end_date_input || ''}
                    onChange={(e) =>
                      setSelectedTournament({ ...selectedTournament, _end_date_input: e.target.value })
                    }
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="edit-active">Active Status</Label>
                <Switch
                  id="edit-active"
                  checked={selectedTournament.is_active}
                  onCheckedChange={(checked) => 
                    setSelectedTournament({ ...selectedTournament, is_active: checked })
                  }
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <Label htmlFor="edit-default">Set as Default Tournament</Label>
                <Switch
                  id="edit-default"
                  checked={selectedTournament.is_default}
                  onCheckedChange={(checked) => 
                    setSelectedTournament({ ...selectedTournament, is_default: checked })
                  }
                />
              </div>
              
              <div className="space-y-2 p-3 border rounded-lg">
                <Label className="flex items-center justify-between">
                  <span>Tournament Logo</span>
                  {selectedTournament.logo_url && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await axios.delete(
                            `${API}/tournaments/${selectedTournament.tournament_id}/logo`,
                            { withCredentials: true }
                          );
                          toast.success('Logo removed');
                          setSelectedTournament({ ...selectedTournament, logo_url: null });
                          fetchTournaments();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to remove logo');
                        }
                      }}
                      className="text-xs text-destructive hover:underline"
                      data-testid="tournament-logo-remove"
                    >
                      Remove
                    </button>
                  )}
                </Label>
                <div className="flex items-center gap-3">
                  {selectedTournament.logo_url ? (
                    <img
                      src={selectedTournament.logo_url}
                      alt="logo"
                      className="h-12 w-12 rounded object-contain border border-border bg-white"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded border border-dashed border-border bg-muted/30 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    data-testid="tournament-logo-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 1024 * 1024) {
                        toast.error('Logo must be under 1 MB');
                        e.target.value = '';
                        return;
                      }
                      const fd = new FormData();
                      fd.append('file', f);
                      try {
                        const res = await axios.post(
                          `${API}/tournaments/${selectedTournament.tournament_id}/logo`,
                          fd,
                          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                        );
                        setSelectedTournament({
                          ...selectedTournament,
                          logo_url: res.data.logo_url,
                        });
                        toast.success('Logo uploaded');
                        fetchTournaments();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Logo upload failed');
                      } finally {
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  PNG/JPG ≤ 1 MB. Shown in the app header when this tournament is selected; falls back to the MPL crest when empty.
                </p>
              </div>

              <Button type="submit" className="w-full">
                Update Tournament
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TournamentManagement;
